import { Avatar, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Building2, MapPin, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PrimaryClinicBadgeProps {
  clinicName?: string;
  clinicLogoUrl?: string | null;
  clinicAddress?: string | null;
  clinicPhone?: string | null;
  /** 'chip' — compact pill for the header. 'card' — full detail card for the dashboard. */
  variant?: 'chip' | 'card';
}

/**
 * The clinic a patient registered/was invited to, labeled "Primary Clinic" — patient accounts are
 * 1:1 with a single clinic today, but the label future-proofs the UI for when a patient can be
 * linked to more than one (see the deferred cross-clinic booking plan).
 */
export function PrimaryClinicBadge({
  clinicName, clinicLogoUrl, clinicAddress, clinicPhone, variant = 'chip',
}: PrimaryClinicBadgeProps) {
  const { t } = useTranslation('portal');
  if (!clinicName) return null;

  if (variant === 'chip') {
    return (
      <Chip
        avatar={
          clinicLogoUrl
            ? <Avatar src={clinicLogoUrl} alt={clinicName} />
            : <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.25)' }}><Building2 size={14} /></Avatar>
        }
        label={
          <Stack spacing={-0.3}>
            <Typography variant="caption" sx={{ opacity: 0.75, lineHeight: 1, fontSize: '0.62rem' }}>
              {t('primaryClinic')}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {clinicName}
            </Typography>
          </Stack>
        }
        sx={{
          height: 'auto',
          py: 0.5,
          px: 0.25,
          bgcolor: 'rgba(255,255,255,0.14)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.25)',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={clinicLogoUrl ?? undefined}
            sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
          >
            <Building2 size={26} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="overline" color="primary" fontWeight={700}>
              {t('yourPrimaryClinic')}
            </Typography>
            <Typography variant="h6" fontWeight={700} noWrap>
              {clinicName}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {clinicAddress && (
                <Stack direction="row" spacing={0.75} alignItems="flex-start">
                  <MapPin size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">{clinicAddress}</Typography>
                </Stack>
              )}
              {clinicPhone && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Phone size={14} style={{ flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">{clinicPhone}</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
